import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import {
  CompareArrows as CompareIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ApiService } from '../../../services/ApiService';

interface VersionComparison {
  from: {
    version: number;
    uploadedBy: string;
    uploadedAt: string;
    size: number;
    title: string;
    versionNotes: string;
  };
  to: {
    version: number;
    uploadedBy: string;
    uploadedAt: string;
    size: number;
    title: string;
    versionNotes: string;
  };
  changes: {
    titleChanged: boolean;
    sizeChanged: boolean;
    sizeDiff: number;
    fileChanged: boolean;
    daysBetween: number;
  };
}

interface DocumentVersionCompareProps {
  documentId: string;
  fromVersion: number;
  toVersion: number;
  onClose?: () => void;
}

const DocumentVersionCompare: React.FC<DocumentVersionCompareProps> = ({
  documentId,
  fromVersion,
  toVersion,
  onClose,
}) => {
  const [comparison, setComparison] = useState<VersionComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchComparison();
  }, [documentId, fromVersion, toVersion]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.get<VersionComparison>(
        `/documents/${documentId}/versions/compare?from=${fromVersion}&to=${toVersion}`
      );
      setComparison(response);
    } catch (err: any) {
      console.error('Error fetching comparison:', err);
      setError(err.message || 'Failed to compare versions');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${bytes < 0 ? '-' : ''}${Math.abs(size)} ${sizes[i]}`;
  };

  const handleDownloadVersion = (version: number) => {
    window.open(`/api/documents/${documentId}/versions/${version}/download`, '_blank');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!comparison) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Version Comparison
        </Typography>
        {onClose && (
          <Button onClick={onClose} size="small">
            Close
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Version From */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Version {comparison.from.version}
              </Typography>
              <Chip label="From" color="default" size="small" />
            </Box>
            
            <Typography variant="body2" paragraph>
              <strong>Title:</strong> {comparison.from.title}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Uploaded by:</strong> {comparison.from.uploadedBy}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Date:</strong> {format(new Date(comparison.from.uploadedAt), 'MMM d, yyyy h:mm a')}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Size:</strong> {formatFileSize(comparison.from.size)}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Notes:</strong> {comparison.from.versionNotes}
            </Typography>
            
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadVersion(comparison.from.version)}
              fullWidth
            >
              Download Version {comparison.from.version}
            </Button>
          </Paper>
        </Grid>

        {/* Arrow */}
        <Grid item xs={12} md={2} display="flex" alignItems="center" justifyContent="center">
          <CompareIcon fontSize="large" color="action" />
        </Grid>

        {/* Version To */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Version {comparison.to.version}
              </Typography>
              <Chip label="To" color="primary" size="small" />
            </Box>
            
            <Typography variant="body2" paragraph>
              <strong>Title:</strong> {comparison.to.title}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Uploaded by:</strong> {comparison.to.uploadedBy}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Date:</strong> {format(new Date(comparison.to.uploadedAt), 'MMM d, yyyy h:mm a')}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Size:</strong> {formatFileSize(comparison.to.size)}
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Notes:</strong> {comparison.to.versionNotes}
            </Typography>
            
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadVersion(comparison.to.version)}
              fullWidth
            >
              Download Version {comparison.to.version}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Changes Summary */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          Changes Summary
        </Typography>
        
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Change Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>
                  <Chip
                    label={comparison.changes.titleChanged ? 'Changed' : 'Unchanged'}
                    color={comparison.changes.titleChanged ? 'warning' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {comparison.changes.titleChanged ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        From: {comparison.from.title}
                      </Typography>
                      <Typography variant="body2">
                        To: {comparison.to.title}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Same title
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell>File Size</TableCell>
                <TableCell>
                  <Chip
                    label={comparison.changes.sizeChanged ? 'Changed' : 'Unchanged'}
                    color={comparison.changes.sizeChanged ? 'warning' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {comparison.changes.sizeChanged ? (
                    <Typography variant="body2">
                      Difference: {formatFileSize(comparison.changes.sizeDiff)}
                      {comparison.changes.sizeDiff > 0 ? ' (increased)' : ' (decreased)'}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Same size
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell>File Content</TableCell>
                <TableCell>
                  <Chip
                    label={comparison.changes.fileChanged ? 'Changed' : 'Identical'}
                    color={comparison.changes.fileChanged ? 'warning' : 'success'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {comparison.changes.fileChanged
                      ? 'File content has been modified'
                      : 'File content is identical (same hash)'}
                  </Typography>
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell>Time Between Versions</TableCell>
                <TableCell>
                  <Chip
                    label={`${comparison.changes.daysBetween} days`}
                    color="default"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {comparison.changes.daysBetween === 0
                      ? 'Same day'
                      : comparison.changes.daysBetween === 1
                      ? '1 day apart'
                      : `${comparison.changes.daysBetween} days apart`}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Download Both */}
      <Box mt={3} display="flex" justifyContent="center">
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={async () => {
            try {
              const formData = new FormData();
              formData.append('versions', JSON.stringify([fromVersion, toVersion]));
              
              const response = await fetch(`/api/documents/${documentId}/versions/bulk-download`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: formData,
              });
              
              if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = window.document.createElement('a');
                a.href = url;
                a.download = `document_${documentId}_versions_${fromVersion}_${toVersion}.zip`;
                window.document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                window.document.body.removeChild(a);
              }
            } catch (err) {
              console.error('Error downloading versions:', err);
            }
          }}
        >
          Download Both Versions as ZIP
        </Button>
      </Box>
    </Paper>
  );
};

export default DocumentVersionCompare;
